import React, { useState } from 'react';
import { SecuritySettings, UpdateSecuritySettingsDto } from '../../types/audit';
import { validatePassword } from '../../utils/security';
import { Input } from '../ui/Input';
import { CheckCircle2, Circle } from 'lucide-react';

interface PasswordPolicyFormProps {
  settings: SecuritySettings;
  onChange: (patch: UpdateSecuritySettingsDto) => void;
}

const Toggle: React.FC<{ label: string; checked: boolean; onToggle: () => void }> = ({ label, checked, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between py-2 text-xs text-slate-300 hover:text-slate-100"
  >
    <span>{label}</span>
    <span className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-sky-600' : 'bg-slate-700'}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </span>
  </button>
);

export const PasswordPolicyForm: React.FC<PasswordPolicyFormProps> = ({ settings, onChange }) => {
  const [sample, setSample] = useState('');
  const result = sample ? validatePassword(sample, settings) : null;

  const rules = [
    { label: `At least ${settings.password_min_length} characters`, ok: sample.length >= settings.password_min_length },
    { label: 'Contains uppercase letter', ok: !settings.password_require_uppercase || /[A-Z]/.test(sample) },
    { label: 'Contains number', ok: !settings.password_require_numbers || /[0-9]/.test(sample) },
    { label: 'Contains special character', ok: !settings.password_require_special || /[^A-Za-z0-9]/.test(sample) },
  ];

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          type="number"
          min={6}
          max={64}
          label="Minimum Length"
          value={settings.password_min_length}
          onChange={e => onChange({ password_min_length: Number(e.target.value) })}
        />
        <Input
          type="number"
          min={0}
          max={24}
          label="Enforce Password History (count)"
          value={settings.enforce_password_history}
          onChange={e => onChange({ enforce_password_history: Number(e.target.value) })}
        />
      </div>
      <div className="divide-y divide-slate-800/60">
        <Toggle
          label="Require Uppercase Letter"
          checked={settings.password_require_uppercase}
          onToggle={() => onChange({ password_require_uppercase: !settings.password_require_uppercase })}
        />
        <Toggle
          label="Require Numbers"
          checked={settings.password_require_numbers}
          onToggle={() => onChange({ password_require_numbers: !settings.password_require_numbers })}
        />
        <Toggle
          label="Require Special Characters"
          checked={settings.password_require_special}
          onToggle={() => onChange({ password_require_special: !settings.password_require_special })}
        />
        <Toggle
          label="Require Two-Factor Authentication (2FA)"
          checked={settings.require_2fa}
          onToggle={() => onChange({ require_2fa: !settings.require_2fa })}
        />
      </div>

      <div className="pt-2">
        <Input
          type="text"
          label="Policy Preview (test a sample password)"
          placeholder="Type a sample password..."
          value={sample}
          onChange={e => setSample(e.target.value)}
        />
        {result && (
          <ul className="mt-2 space-y-1">
            {rules.map((rule, i) => (
              <li key={i} className={`flex items-center gap-1.5 text-[11px] ${rule.ok && (sample.length > 0) ? 'text-emerald-400' : 'text-slate-500'}`}>
                {rule.ok && sample.length > 0 ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                {rule.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
